import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  DocumentGenerationTemplate,
  DocumentGenerationTemplate_CreateDocumentGenerationTemplateInput,
  DocumentGenerationTemplate_GetAllDocumentGenerationTemplatesDocument,
  DocumentGenerationTemplate_UpdateDocumentGenerationTemplateInput,
  DocumentGenerationTemplateType,
  EstateInputDataFormat,
  EstateTask_GetAllEstateTasksDocument,
  SignatureType,
  UploadPath,
  useDocumentGenerationTemplate_CreateDocumentGenerationTemplateMutation,
  useDocumentGenerationTemplate_UpdateDocumentGenerationTemplateMutation,
  useDocumentTag_GetAllDocumentTagsQuery,
  useEstateInputDataFormat_GetAllEstateInputDataFormatsQuery,
} from 'queries';
import * as yup from 'yup';

import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';
import Spinner from 'common/spinner';
import {
  signatureTypeSelectOptions,
  uploadPathSelectOptions,
} from 'pages/common/document/document-upload-modal';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import EstateInputDataFormatsMultipleSelectCheckmarks from './estate-input-data-formats-multiple-select-checkmarks';

type Props = {
  isEditing: boolean;
  selectedDocumentGenerationTemplate?: DocumentGenerationTemplate | undefined;
  onDownloadPreview?: () => void;
  isSaveDisabled: boolean;
  onExitClick: () => void;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
    '& input': {
      paddingRight: '10px',
      padding: '1vh 2vh',
    },
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    textAlign: 'left',
  },
  submitButton: {
    paddingTop: '10px',
  },
}));

type DocumentGenerationTemplateFormValues = {
  name: string;
  type: DocumentGenerationTemplateType;
  documentTagId: string;
  uploadPath: UploadPath;
  signatureType?: SignatureType | null;
  skipPrinting: boolean;
  externalDocumentIdentifier: string;
  instructions?: string | null;
  estateInputDataFormatIds?: string[] | null;
};

const requiredTextFields = ['name', 'externalDocumentIdentifier'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const requiredTextFieldValidation = requiredTextFields.reduce((acc: any, val: string) => {
  acc[val] = yup.string().required('Required');
  return acc;
}, {});

const schema = yup.object().shape({
  ...requiredTextFieldValidation,
});

export default function DocumentGenerationTemplateForm({
  isEditing,
  selectedDocumentGenerationTemplate,
  onDownloadPreview,
  isSaveDisabled,
  onExitClick,
}: Props) {
  const classes = useStyles();

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setValue,
  } = useForm<DocumentGenerationTemplateFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      name: selectedDocumentGenerationTemplate?.name || '',
      type: selectedDocumentGenerationTemplate?.type || undefined,
      documentTagId: selectedDocumentGenerationTemplate?.documentTagId || undefined,
      uploadPath: selectedDocumentGenerationTemplate?.uploadPath || undefined,
      signatureType: selectedDocumentGenerationTemplate?.signatureType || null,
      skipPrinting: selectedDocumentGenerationTemplate?.skipPrinting || false,
      externalDocumentIdentifier:
        selectedDocumentGenerationTemplate?.externalDocumentIdentifier || '',
      instructions: selectedDocumentGenerationTemplate?.instructions || null,
      estateInputDataFormatIds:
        selectedDocumentGenerationTemplate?.documentGenerationTemplateToEstateInputDataFormats
          ? selectedDocumentGenerationTemplate.documentGenerationTemplateToEstateInputDataFormats.map(
              (format) => format.estateInputDataFormatId,
            )
          : [],
    },
  });

  const [
    createDocumentGenerationTemplate,
    { loading: executingCreateDocumentGenerationTemplateMutation },
  ] = useDocumentGenerationTemplate_CreateDocumentGenerationTemplateMutation({
    refetchQueries: [
      { query: DocumentGenerationTemplate_GetAllDocumentGenerationTemplatesDocument },
    ],
    onCompleted: () => {
      showToast({
        message: 'Document generation template created.',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error creating document generation template: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [
    updateDocumentGenerationTemplate,
    { loading: executingUpdateDocumentGenerationTemplateMutation },
  ] = useDocumentGenerationTemplate_UpdateDocumentGenerationTemplateMutation({
    refetchQueries: [
      { query: DocumentGenerationTemplate_GetAllDocumentGenerationTemplatesDocument },
      {
        query: EstateTask_GetAllEstateTasksDocument,
        variables: {
          input: {
            isArchived: false,
          },
        },
      },
      {
        query: EstateTask_GetAllEstateTasksDocument,
        variables: {
          input: {
            isArchived: true,
          },
        },
      },
    ],
    onCompleted: () => {
      showToast({
        message: 'Document generation template updated',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating document generation template: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: documentTagsData, loading: documentTagsDataLoading } =
    useDocumentTag_GetAllDocumentTagsQuery({
      onError: (error) => {
        showAlert({
          message: 'Error getting all document tags: ' + error.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { data: estateInputDataFormatData, loading: estateInputDataFormatDataLoading } =
    useEstateInputDataFormat_GetAllEstateInputDataFormatsQuery({
      onError: (error) => {
        showAlert({
          message: 'Error getting all estate input data formats: ' + error.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const submitForm = async ({ ...formData }: DocumentGenerationTemplateFormValues) => {
    if (
      !formData.name ||
      !formData.type ||
      !formData.documentTagId ||
      !formData.uploadPath ||
      !formData.externalDocumentIdentifier
    ) {
      showAlert({
        message: 'Missing field(s) required',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    if (
      formData.uploadPath === UploadPath.DocumentsForSignature &&
      formData.signatureType === SignatureType.NoSignature
    ) {
      showAlert({
        message: 'Error uploading file: Signature Type is required for Documents for Signature.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    if (selectedDocumentGenerationTemplate) {
      const input: DocumentGenerationTemplate_UpdateDocumentGenerationTemplateInput = {
        ...formData,
        id: selectedDocumentGenerationTemplate.id,
      };

      updateDocumentGenerationTemplate({
        variables: {
          input,
        },
      });
    } else {
      const input: DocumentGenerationTemplate_CreateDocumentGenerationTemplateInput = {
        ...formData,
        signatureType: formData!.signatureType || null,
        estateInputDataFormatIds: formData!.estateInputDataFormatIds || null,
      };

      createDocumentGenerationTemplate({
        variables: {
          input,
        },
      });
    }
  };

  const handleDownloadPreview = async () => {
    onDownloadPreview!();
  };

  if (
    !documentTagsData ||
    documentTagsDataLoading ||
    !estateInputDataFormatData ||
    estateInputDataFormatDataLoading
  ) {
    return <Spinner />;
  }

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        {isEditing ? 'Edit' : 'Create'} Document Generation Template
      </Typography>
      <form
        id="document-generation-template-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="name"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    autoFocus
                    inputProps={{ maxLength: 80 }}
                    label="Name"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="type"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    value={field.value || ''}
                    options={[
                      { label: 'Letter', value: DocumentGenerationTemplateType.Letter },
                      { label: 'Form', value: DocumentGenerationTemplateType.Form },
                    ]}
                    label="Type"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="documentTagId"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    required={true}
                    value={field.value || ''}
                    options={
                      documentTagsData.data!.map((tag) => ({
                        label: tag.name,
                        value: tag.id,
                      })) || []
                    }
                    label="Document Tag"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="uploadPath"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    value={field.value || ''}
                    inputRef={ref}
                    required={true}
                    options={uploadPathSelectOptions.filter(
                      (option) => option.value !== UploadPath.Uploads,
                    )}
                    label="Upload Path"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="signatureType"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormSelectField
                    {...field}
                    inputRef={ref}
                    value={field.value || ''}
                    required={true}
                    options={signatureTypeSelectOptions}
                    label="Signature Type"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Typography variant="caption">Skip Printing</Typography>

              <Controller
                name="skipPrinting"
                control={control}
                render={({ field: { ref, onChange, value, ...field } }) => (
                  <Checkbox
                    {...field}
                    inputRef={ref}
                    onChange={(evt) => onChange(evt.target.checked)}
                    color="primary"
                    checked={value}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid item xs={12} sm={12}>
              <Controller
                name="externalDocumentIdentifier"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    name="externalDocumentIdentifier"
                    inputProps={{ maxLength: 80 }}
                    label="External Document Id"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid item xs={12} sm={12}>
              <Controller
                name="instructions"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    name="instructions"
                    label="Instructions"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="estateInputDataFormatIds"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <EstateInputDataFormatsMultipleSelectCheckmarks
                    inputRef={ref}
                    value={field.value || []}
                    options={estateInputDataFormatData.data as EstateInputDataFormat[]}
                    label="Estate Input Data Formats"
                    onSelectChange={(selections: string[]) => {
                      setValue('estateInputDataFormatIds', selections);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={clsx(classes.container, classes.submitButton)}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Button
                type="submit"
                form="document-generation-template-form"
                color="secondary"
                variant="contained"
                disabled={
                  isSubmitting ||
                  isSaveDisabled ||
                  executingCreateDocumentGenerationTemplateMutation ||
                  executingUpdateDocumentGenerationTemplateMutation
                }
              >
                Save
              </Button>
            </Grid>
            {isEditing && (
              <>
                <Grid className={classes.inputGrid} item xs={12} sm={6}>
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={isSubmitting || isSaveDisabled}
                    onClick={handleDownloadPreview}
                  >
                    Download Preview
                  </Button>
                </Grid>
                <Typography variant="caption">
                  Please save before downloading preview for latest updates to take effect.
                </Typography>
              </>
            )}
          </Grid>
        </div>
      </form>
    </>
  );
}
